import { useAuth } from "contexts/AuthContext";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppstoreOutlined,
  BankOutlined,
  CloseOutlined,
  CreditCardOutlined,
  DollarOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { buildOlreadiaNav } from "../../nav/navData";
import paths from "../../nav/paths";
import { useWorkspace } from "contexts/WorkspaceContext";
import { hasAdminAccess, membershipUserId } from "workspaces/workspaceUtils";

const NAV_ICONS = {
  profile:        "id-card",
  "account-profile": "id-card",
  "job-search": "search",
  "talent-search": "search",
  "job-posts": "briefcase",
  applications: "list-alt",
  interviews: "calendar-check",
  "create-job": "plus-square",
  "professional-profiles": "user-tie",
  "background-checks": "shield-alt",
  organizations: "building",
  invitations: "envelope-open-text",
  reports: "chart-line",
  website: "globe",
  "start-professional-verification": "user-shield",
  chat: "comments",
  inbox: "envelope",
  "chat-space": "comments",
  "email-messages": "envelope",
  "access-control": "shield-alt",
  workspaces: "th-large",
  wallet: "credit-card",
  partners: "handshake",
  settings: "cog",
  feedback: "comment",
  "help-support": "headset",
  legal: "file-contract",
};

export default function Sidebar({ mobileSbOpen, onCloseMobile }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { selectedWorkspace, selectedWorkspaceUse } = useWorkspace();
  const userId = user?.id || user?.userId || "";
  const selectedMembership = (selectedWorkspace?.users || []).find(
    (membership) => membershipUserId(membership) === userId,
  );
  const canManageWorkspace =
    hasAdminAccess(selectedMembership) ||
    hasAdminAccess(selectedWorkspace) ||
    String(selectedWorkspace?.accountType || "").toUpperCase() === "PERSONAL";
  const { topNav, bottomNav } = buildOlreadiaNav({
    selectedWorkspace,
    selectedWorkspaceUse,
    canManageWorkspace,
  });

  const isAct = (to) => pathname === to || pathname.startsWith(`${to}/`);

  const iconNode = (item) => (
    <span className="pm-ni-ico">
      {item.id === "workspaces" ? (
        <AppstoreOutlined />
      ) : item.id === "workspace-context" ? (
        <AppstoreOutlined />
      ) : item.id === "organization" ? (
        <BankOutlined />
      ) : item.id === "subscriptions" ? (
        <CreditCardOutlined />
      ) : item.id === "payment-methods" ? (
        <CreditCardOutlined />
      ) : item.id === "charges" ? (
        <DollarOutlined />
      ) : (
        <FontAwesomeIcon icon={NAV_ICONS[item.id] || "circle"} />
      )}
    </span>
  );

  const renderNavItem = (item) => {
    if (item.section) {
      return (
        <div key={item.id} className="pm-glbl">
          {item.label}
        </div>
      );
    }

    return (
      <div
        key={item.id}
        className={`pm-ni${isAct(item.to) ? " act" : ""}`}
        onClick={() => { navigate(item.to); onCloseMobile?.(); }}
      >
        {iconNode(item)}
        <span className="pm-ni-lbl">{item.label}</span>
        {item.badge && <span className="pm-badge">{item.badge}</span>}
        <span className="pm-tip">{item.label}</span>
      </div>
    );
  };

  return (
    <aside
      className={`pm-sb${collapsed ? " col" : ""}${mobileSbOpen ? " mobile-open" : ""}`}
      style={{
        flex:     collapsed ? "0 0 44px" : "0 0 256px",
        width:    collapsed ? 44 : 256,
        minWidth: collapsed ? 44 : 256,
        maxWidth: collapsed ? 44 : 256,
      }}
    >
      <div className="pm-sb-head">
        <button
          type="button"
          className="pm-logo-wrap"
          onClick={() => navigate(paths.pmDashboard)}
          aria-label="Go to home"
        >
          <img src="/logo1.png" alt="Olreadia" className="pm-logo-img" />
        </button>
        <button
          type="button"
          className="pm-sb-toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <MenuOutlined />
        </button>
        <button
          type="button"
          className="pm-sb-close"
          onClick={onCloseMobile}
          aria-label="Close menu"
        >
          <CloseOutlined />
        </button>
      </div>

      <nav className="pm-nav">
        {topNav.map(renderNavItem)}

        <div className="pm-nav-divider" />

        {bottomNav.map(renderNavItem)}
      </nav>

      <div className="pm-sb-foot">
        <div className="pm-foot-title">Account</div>
        <div
          className="pm-foot-ni pm-foot-danger"
          onClick={() => logout(false, paths.landing)}
        >
          <span style={{ fontSize: 14 }}>
            <FontAwesomeIcon icon="right-from-bracket" />
          </span>
          <span className="pm-foot-text">Sign out</span>
        </div>
      </div>
    </aside>
  );
}
